import config from '@/config';
import { Routes } from '@/constants';
import {
  cancelStripeSubscription,
  CancelStripeSubscriptionPayload,
  fetchDefaultPromoCode,
  fetchPricingTableValues,
  fetchPurchaseFeedbackOptions,
  fetchUserStripeCustomerInfo,
  generateBillingPortalLink,
  getOffers,
  PostgrestEndpoints,
  submitPurchaseFeedback,
} from '@/requests';
import { Offer, PricingTableCTA, Region } from '@/types';
import { camelCaseKeys } from '@/utils';

import { postgrestApi } from '../postgrest-api';
import { rtkQueryError } from '../store.utils';
import { PollEndpointStatuses } from './payment-api.constants';
import { generatePollingQuery } from './payment-api.utils';

type BasePollingResult = {
  status: (typeof PollEndpointStatuses)[keyof typeof PollEndpointStatuses];
};

type GenerateBillingPortalLinkResult = {
  url: string;
} & BasePollingResult;

type PollForCheckoutSessionCompletionResult = {
  seatCap: number;
  usedSeats: number;
  organizationName: string;
  organizationId: string;
} & BasePollingResult;

type FetchPricingTableValuesResult = {
  amount?: number;
  callToAction: PricingTableCTA;
  callToActionText: string;
  currency?: string;
  featureList: string[];
  name: string;
  productDescription: string;
  recurringInterval?: string;
  isHighlight?: boolean;
  highlightText?: string;
  promotionCode?: string;
  usageLimit?: number;
  stripeProductId?: string;
}[];

type FetchDefaultPromoCodeResult = {
  amountOff?: number;
  percentOff?: number;
  code: string;
  name: string;
  currency?: string;
  expiresAt?: string;
};

type FetchUserStripeCustomerInfoResult = {
  hasBillingPortalAccess: boolean;
  hasCurrentStripeSubscription: boolean;
  hasDirectCancelButton: boolean;
};

export const paymentApiPostgrest = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    getOffer: builder.query<Offer, { sku: string }>({
      query: ({ sku }) => getOffers({ skus: [sku] }),
      transformResponse: (response: any) => camelCaseKeys<Offer>(response?.[0]),
    }),
    fetchUserStripeCustomerInfo: builder.query<
      FetchUserStripeCustomerInfoResult,
      void
    >({
      query: fetchUserStripeCustomerInfo,
      transformResponse: (response: any) => ({
        hasBillingPortalAccess: !!response[0]?.has_billing_portal,
        hasCurrentStripeSubscription:
          !!response[0]?.has_current_stripe_subscription,
        hasDirectCancelButton: !!response[0]?.has_direct_cancel_button,
      }),
    }),
    generateBillingPortalLink: builder.mutation<
      GenerateBillingPortalLinkResult,
      void
    >({
      queryFn: async (params, redux, extraOptions, baseQuery) => {
        try {
          const { data, error } = await generatePollingQuery<{ url: string }>({
            baseQuery,
            queryArgs: generateBillingPortalLink({
              redirectUrl: `${window.location.origin}${Routes.PROFILE}`,
            }),
          });
          if (error) return { error };

          return { data };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    fetchPricingTableValues: builder.query<
      FetchPricingTableValuesResult,
      { region: Region; metadata?: Record<string, string> }
    >({
      query: fetchPricingTableValues,
      transformResponse: (response: any) => response.map(camelCaseKeys),
    }),
    fetchDefaultPromoCode: builder.query<
      FetchDefaultPromoCodeResult,
      { region: Region }
    >({
      query: fetchDefaultPromoCode,
      transformResponse: camelCaseKeys,
    }),
    fetchPurchaseFeedbackOptions: builder.query<
      {
        howdyhauOptionId: string;
        label: string;
        detailFieldLabel: string;
        detailFieldPlaceholder?: string;
        sortOrder: number;
      }[],
      void
    >({
      query: fetchPurchaseFeedbackOptions,
      transformResponse: (response: any) => camelCaseKeys(response.options),
    }),
    submitPurchaseFeedback: builder.mutation<
      void,
      {
        howdyhauOptionId: string;
        detailResponse: string;
        checkoutSessionId: string;
      }
    >({
      query: submitPurchaseFeedback,
    }),
    hasSubmittedPurchaseFeedback: builder.query<boolean, void>({
      query: () => ({
        url: PostgrestEndpoints.HAS_SUBMITTED_PURCHASE_FEEDBACK,
        method: 'POST',
      }),
      transformResponse: (response: any) => !!response?.message?.has_submitted,
    }),
    cancelStripeSubscription: builder.mutation<
      void,
      CancelStripeSubscriptionPayload
    >({
      query: cancelStripeSubscription,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          /* 
            Sub cancellation is an async operation, so we
            eagerly toggle hasDirectCancelButton to false on submission.
          */
          dispatch(
            paymentApiPostgrest.util.updateQueryData(
              'fetchUserStripeCustomerInfo',
              undefined,
              draft => {
                draft.hasDirectCancelButton = false;
              },
            ),
          );
        } catch (error) {
          rtkQueryError(error);
        }
      },
    }),
    fetchBillingAdminOrgs: builder.query<string[], void>({
      query: () => ({
        url: PostgrestEndpoints.BILLING_ADMIN_ORGS,
        method: 'GET',
      }),
      transformResponse: (response: { organization_id: string }[]) =>
        response?.map(({ organization_id }) => organization_id) || [],
    }),
    fetchOrgBillingPortalLink: builder.query<
      string,
      { organizationId: string; redirectUrl: string }
    >({
      queryFn: async (
        { organizationId, redirectUrl },
        redux,
        extraOptions,
        baseQuery,
      ) => {
        const { data, error } = await generatePollingQuery<{ url: string }>({
          baseQuery,
          queryArgs: {
            url: PostgrestEndpoints.ORG_BILLING_PORTAL_LINK,
            method: 'POST',
            body: {
              organization_id: organizationId,
              redirect_url: redirectUrl,
            },
          },
        });
        if (error) return { error };

        return { data: data.url };
      },
    }),
    createCheckoutSession: builder.mutation<
      {
        checkoutSessionTaskId: string;
      },
      {
        stripeProductId: string;
        redirectUrl?: string;
        country?: string;
        organizationId?: string;
      }
    >({
      query: ({ stripeProductId, redirectUrl, country, organizationId }) => ({
        url: PostgrestEndpoints.CREATE_CHECKOUT_SESSION,
        method: 'POST',
        body: {
          stripe_product_id: stripeProductId,
          ...(redirectUrl && { redirect_url: redirectUrl }),
          ...(country && {
            data: {
              country_code: country,
              ...(organizationId && { organization_id: organizationId }),
            },
          }),
        },
      }),
      transformResponse: (response: any) => ({
        checkoutSessionTaskId: response.checkout_session_task_id,
      }),
    }),
    pollForCheckoutSessionUrl: builder.query<
      { url: string },
      { checkoutSessionTaskId: string }
    >({
      queryFn: async (
        { checkoutSessionTaskId },
        redux,
        extraOptions,
        baseQuery,
      ) => {
        const { data, error } = await generatePollingQuery<{ url: string }>({
          baseQuery,
          queryArgs: {
            url: PostgrestEndpoints.GET_CHECKOUT_SESSION_URL,
            method: 'POST',
            body: { checkout_session_task_id: checkoutSessionTaskId },
          },
        });
        if (error) return { error };

        return { data };
      },
    }),
    createPendingB2BCheckout: builder.mutation<
      { checkoutSessionTaskId: string },
      { countryCode: string; redirectUrl?: string; organizationId?: string }
    >({
      query: ({ countryCode, organizationId, redirectUrl }) => ({
        url: PostgrestEndpoints.CREATE_PENDING_B2B_CHECKOUT,
        method: 'POST',
        body: {
          stripe_environment_id: config.STRIPE_ENVIRONMENT_ID,
          country_code: countryCode,
          ...(redirectUrl && { redirect_url: redirectUrl }),
          data: {
            ...(organizationId && { organization_id: organizationId }),
          },
        },
      }),
      transformResponse: camelCaseKeys,
    }),
    pollForCheckoutSessionCompletion: builder.query<
      PollForCheckoutSessionCompletionResult,
      { checkoutSessionId: string }
    >({
      queryFn: async (
        { checkoutSessionId },
        redux,
        extraOptions,
        baseQuery,
      ) => {
        const { data, error } = await generatePollingQuery<{
          seat_cap: number;
          used_seats: number;
          organization_name: string;
          organization_id: string;
        }>({
          baseQuery,
          queryArgs: {
            url: PostgrestEndpoints.STRIPE_PURCHASE_STATUS,
            method: 'POST',
            body: { checkout_session_id: checkoutSessionId },
          },
        });
        if (error) return { error };

        return {
          data: {
            seatCap: data.seat_cap,
            usedSeats: data.used_seats,
            organizationName: data.organization_name,
            organizationId: data.organization_id,
            status: data.status,
          },
        };
      },
    }),
  }),
});

export const {
  useGetOfferQuery,
  useFetchUserStripeCustomerInfoQuery,
  useGenerateBillingPortalLinkMutation,
  useFetchPricingTableValuesQuery,
  useFetchDefaultPromoCodeQuery,
  useFetchPurchaseFeedbackOptionsQuery,
  useSubmitPurchaseFeedbackMutation,
  useHasSubmittedPurchaseFeedbackQuery,
  useCancelStripeSubscriptionMutation,
  useFetchBillingAdminOrgsQuery,
  useLazyFetchOrgBillingPortalLinkQuery,
  useCreatePendingB2BCheckoutMutation,
  useLazyPollForCheckoutSessionCompletionQuery,
  useCreateCheckoutSessionMutation,
  useLazyPollForCheckoutSessionUrlQuery,
} = paymentApiPostgrest;
